import { action, computed, makeAutoObservable, observable } from 'mobx';
import { CmsTranslationDataModelType } from 'src/api/config/cmsTranslations/getAllCmsTranslations';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { SeoFormState, SeoPageType } from 'src/main/cms/SeoManagement/SeoForm/SeoFormState';

export class SeoItemState {
    @observable public showDeletePopup: boolean = false;
    @observable public isActive: boolean = this.seoItem.is_active;

    public constructor(
        private seoItem: SeoPageType,
        private seoPagesState: SeoFormState,
        private readonly trpcClient: TrpcClient
    ) {
        makeAutoObservable(this);
    }

    @computed public get seoItemData(): SeoPageType {
        return this.seoItem;
    }

    @computed public get promoId(): number {
        return this.seoItem.id;
    }

    @computed public get isEdited(): boolean {
        return this.seoItem.id === this.seoPagesState.editedSeoPageId;
    }

    @computed public get itemTranslations(): Array<CmsTranslationDataModelType> {
        return this.seoPagesState.cmsTranslationsSeoPagesList.filter((item) => item.component_id === this.seoItem.id);
    }

    @action public handleEdit = async (): Promise<void> => {
        this.seoPagesState.resetForm();
        this.seoPagesState.resetAllTranslationForms();
        this.seoPagesState.setSeoPagesTranslations(this.itemTranslations);
        this.seoPagesState.setEditSeoPage(this.seoItem);
    };

    @action public handleDuplicate = async (): Promise<void> => {
        this.seoPagesState.resetForm();
        this.seoPagesState.resetAllTranslationForms();
        this.seoPagesState.isDuplicateMode = true;
        this.seoPagesState.setSeoPagesTranslations(this.itemTranslations);
        this.seoPagesState.setEditSeoPage(this.seoItem);
    };

    @action public setActive = async (): Promise<void> => {
        const updatedItemData = {
            ...this.seoItem,
            is_active: !this.seoItem.is_active,
        };

        this.isActive = updatedItemData.is_active;

        try {
            await this.trpcClient.client.seoPages.updateSeoPage.mutate({
                indexNow: this.seoPagesState.indexNow,
                data: updatedItemData,
                id: this.seoItem.id,
            });
            this.seoItem = updatedItemData;
            await this.seoPagesState.seoPagesResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public handleDeleteSeoPage = async (): Promise<void> => {
        const removeResult = await this.trpcClient.client.seoPages.deleteSeoPage.mutate({
            id: this.seoItem.id,
        });

        if (removeResult.body === undefined) {
            console.log('Error deleting seo content');
        } else {
            for (const translation of this.itemTranslations) {
                await this.seoPagesState.cmsTranslationsState.handleDeleteApiCall(translation.id);
            }
            await this.seoPagesState.cmsTranslationsState.translationsResource.refresh();
            await this.seoPagesState.seoPagesResource.refresh();
            this.seoPagesState.resetForm();
        }
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };
}
